// src/component/HomeHeroCarousel.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Keyboard, EffectFade, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

const HeroCarousel = ({ slides = [] }) => {
  const sliderRef = useRef(null);

  // Если у пользователя включено "уменьшение анимации" — отключим автоплей
  const reduceMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Проигрываем видео только на активном слайде
  const syncVideos = (swiper) => {
    try {
      const root = swiper?.el;
      if (!root) return;
      root.querySelectorAll("video").forEach((v) => {
        try { v.pause(); } catch {}
      });
      const active = swiper.slides?.[swiper.activeIndex];
      active?.querySelectorAll?.("video")?.forEach((v) => {
        v.muted = true;
        v.playsInline = true;
        try { v.play().catch(() => {}); } catch {}
      });
    } catch {}
  };

  useEffect(() => {
    const sw = sliderRef.current?.swiper;
    if (!sw) return;
    syncVideos(sw);
  }, []);

  return (
    <section className="relative w-full max-w-7xl mx-auto py-4 px-1 sm:px-0 overflow-hidden">
      <div className="group relative w-full">
        <Swiper
          modules={[Navigation, Autoplay, Keyboard, EffectFade, A11y]}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          speed={650}
          autoplay={
            !reduceMotion && slides.length > 1
              ? { delay: 6000, disableOnInteraction: false, pauseOnMouseEnter: true }
              : false
          }
          loop={slides.length > 1}
          keyboard={{ enabled: true, onlyInViewport: true }}
          navigation={{ nextEl: ".hero-next", prevEl: ".hero-prev" }}
          preloadImages={false}
          watchSlidesProgress
          className="rounded-2xl shadow-lg w-full"
          ref={sliderRef}
          onSlideChange={(sw) => syncVideos(sw)}
        >
          {slides.map((s, i) => (
            <SwiperSlide key={i} style={{ willChange: "opacity" }}>
              <div
                className="relative overflow-hidden rounded-2xl w-full"
                style={{ minHeight: 260, height: "clamp(260px, 60vw, 500px)" }}
              >
                {/* фон: фото/видео */}
                {s.type === "video" ? (
                  <video
                    className="absolute inset-0 h-full w-full object-cover"
                    src={s.src}
                    muted
                    loop
                    autoPlay
                    playsInline
                    poster={s.poster}
                  />
                ) : (
                  <img
                    className="absolute inset-0 h-full w-full object-cover"
                    src={s.src}
                    alt={s.title || "Баннер"}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    sizes="100vw"
                  />
                )}

                {/* адаптивный градиент к стороне контента для читаемости */}
                {!s.light && (
                  <div
                    className={`absolute inset-0 pointer-events-none ${
                      s.alignRight ? "bg-gradient-to-l" : "bg-gradient-to-r"
                    } from-black/55 via-black/25 to-transparent`}
                  />
                )}

                {/* ВЕРТИКАЛЬНОЕ ЦЕНТРИРОВАНИЕ КОНТЕНТА */}
                <div
                  className={`absolute inset-0 z-10 flex items-center ${
                    s.alignRight ? "justify-end pr-6 sm:pr-10" : "justify-start pl-6 sm:pl-10"
                  }`}
                >
                  <div
                    className={`max-w-xl ${
                      s.light ? "text-black" : "text-white"
                    } ${s.alignRight ? "text-right items-end" : "text-left items-start"} flex flex-col`}
                    style={{ textShadow: s.light ? "none" : "0 1px 8px rgba(0,0,0,.35)" }}
                  >
                    {s.kicker && (
                      <div className="mb-1 text-xs sm:text-sm font-semibold uppercase tracking-wider opacity-90">
                        {s.kicker}
                      </div>
                    )}

                    {s.title && (
                      <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                        {s.title}
                      </h2>
                    )}

                    {s.description && (
                      <p className="mt-3 text-sm sm:text-base md:text-lg opacity-95 max-w-prose">
                        {s.description}
                      </p>
                    )}

                    {s.link && (
                      <a
                        href={s.link}
                        className={`mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-lg font-semibold transition
                          ${
                            s.light
                              ? "bg-black text-white hover:bg-neutral-800 focus-visible:ring-neutral-500/40"
                              : "bg-orange-500 text-white hover:bg-orange-600 focus-visible:ring-orange-300"
                          } focus:outline-none focus-visible:ring-2`}
                      >
                        {s.btn ?? "Подробнее"}
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}

          {/* улучшенные стрелки */}
          <button
            className="hero-prev pointer-events-auto hidden sm:flex items-center justify-center
                       absolute left-2 top-1/2 -translate-y-1/2 z-20
                       h-11 w-11 rounded-full bg-white/80 text-gray-900
                       border border-white/60 backdrop-blur-md shadow-lg
                       opacity-0 group-hover:opacity-100 transition
                       hover:bg-white hover:text-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300"
            aria-label="Назад"
            tabIndex={-1}
          >
            <svg width="22" height="22" viewBox="0 0 24 24">
              <path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className="hero-next pointer-events-auto hidden sm:flex items-center justify-center
                       absolute right-2 top-1/2 -translate-y-1/2 z-20
                       h-11 w-11 rounded-full bg-white/80 text-gray-900
                       border border-white/60 backdrop-blur-md shadow-lg
                       opacity-0 group-hover:opacity-100 transition
                       hover:bg-white hover:text-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300"
            aria-label="Вперёд"
            tabIndex={-1}
          >
            <svg width="22" height="22" viewBox="0 0 24 24">
              <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </Swiper>
      </div>
    </section>
  );
};

export default HeroCarousel;
